import {
    IonBadge,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    calendarOutline,
    chevronForward,
    locationOutline,
    searchOutline,
    trophyOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Competition, getCompetitions } from "../services/mexfbaApi";
import "./Tournment.css";

type CompetitionFilter = "all" | "active" | "upcoming" | "finished";
const HIDDEN_COMPETITION_IDS = new Set([
    "67bc1b79c6a4c"
]);

function parseDate(date?: string): Date | null {
    if (!date) {
        return null;
    }

    const normalizedDate =
        date.length === 10 ? `${date}T12:00:00` : date;

    const parsedDate = new Date(normalizedDate);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function getToday(): Date {
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    return today;
}
function belongsToCurrentYear(competition: Competition): boolean {
    const currentYear = new Date().getFullYear();
    const firstDayOfYear = new Date(currentYear, 0, 1);
    const lastDayOfYear = new Date(currentYear, 11, 31, 23, 59, 59);

    const startDate = parseDate(competition.fechaI);
    const endDate = parseDate(competition.fechaF);

    /*
        Si hay fecha de inicio y final, la competencia aparece
        cuando su temporada se cruza con cualquier fecha del año actual.
    */
    if (startDate && endDate) {
        return startDate <= lastDayOfYear && endDate >= firstDayOfYear;
    }

    /*
        Si faltan fechas, se intenta identificar el año en el nombre.
        Ejemplo: "Nacional 2026".
    */
    return competition.nombre.includes(String(currentYear));
}

function getCompetitionStatus(
    competition: Competition
): Exclude<CompetitionFilter, "all"> {
    const today = getToday();
    const startDate = parseDate(competition.fechaI);
    const endDate = parseDate(competition.fechaF);

    if (endDate && endDate < today) {
        return "finished";
    }

    if (startDate && startDate > today) {
        return "upcoming";
    }

    return "active";
}

function getStatusLabel(status: CompetitionFilter): string {
    const labels: Record<CompetitionFilter, string> = {
        all: "Todas",
        active: "Activa",
        upcoming: "Próxima",
        finished: "Finalizada"
    };

    return labels[status];
}

function getSeasonDateText(competition: Competition): string {
    const startDate = parseDate(competition.fechaI);
    const endDate = parseDate(competition.fechaF);

    if (!startDate && !endDate) {
        return "Fechas por confirmar";
    }

    const formatter = new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    if (startDate && endDate) {
        return `${formatter.format(startDate)} — ${formatter.format(endDate)}`;
    }

    return startDate
        ? `Inicia ${formatter.format(startDate)}`
        : `Finaliza ${formatter.format(endDate as Date)}`;
}

function getStatusOrder(status: CompetitionFilter): number {
    const order: Record<CompetitionFilter, number> = {
        active: 1,
        upcoming: 2,
        finished: 3,
        all: 4
    };

    return order[status];
}

const Tournment: React.FC = () => {
    const history = useHistory();

    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState<CompetitionFilter>("all");

    const loadCompetitions = useCallback(async function (
        forceRefresh = false
    ) {
        try {
            setError("");

            if (!forceRefresh) {
                setLoading(true);
            }

            const data = await getCompetitions(forceRefresh);

            setCompetitions(data);
        } catch (requestError) {
            console.error(
                "No fue posible cargar las competencias:",
                requestError
            );

            setError(
                "No fue posible cargar las competencias. Intenta actualizar nuevamente."
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(function () {
        loadCompetitions();
    }, [loadCompetitions]);

const filteredCompetitions = useMemo(function () {
    const normalizedQuery = query.trim().toLocaleLowerCase("es-MX");

    return competitions
        .filter(function (competition) {
            return !HIDDEN_COMPETITION_IDS.has(
                String(competition.id)
            );
        })
        .filter(belongsToCurrentYear)
        .filter(function (competition) {
            const status = getCompetitionStatus(competition);

            const matchesFilter =
                filter === "all" || status === filter;

            const searchText = [
                competition.nombre,
                competition.sede,
                competition.fechaI?.slice(0, 4),
                competition.fechaF?.slice(0, 4)
            ]
                .filter(Boolean)
                .join(" ")
                .toLocaleLowerCase("es-MX");

            const matchesQuery =
                !normalizedQuery ||
                searchText.includes(normalizedQuery);

            return matchesFilter && matchesQuery;
        })
        .sort(function (firstCompetition, secondCompetition) {
            const firstStatus =
                getCompetitionStatus(firstCompetition);

            const secondStatus =
                getCompetitionStatus(secondCompetition);

            const statusDifference =
                getStatusOrder(firstStatus) -
                getStatusOrder(secondStatus);

            if (statusDifference !== 0) {
                return statusDifference;
            }

            return firstCompetition.nombre.localeCompare(
                secondCompetition.nombre,
                "es-MX"
            );
        });
}, [competitions, filter, query]);


    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadCompetitions(true);

        event.detail.complete();
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="competitions-toolbar">
                    <IonTitle>Competencias</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Buscar en MexFBA"
                            routerLink="/search"
                        >
                            <IonIcon icon={searchOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="competitions-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando competencias..."
                    />
                </IonRefresher>

                <IonHeader collapse="condense">
                    <IonToolbar>
                        <IonTitle size="large">Competencias</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <section className="competitions-hero">
                    <span className="competitions-hero__eyebrow">
                        MEXFBA
                    </span>

                    <h1>Explora el football nacional</h1>

                    <p>
                        Consulta calendarios, equipos, tablas y estadísticas
                        de cada competencia.
                    </p>
                </section>

                <section className="competitions-section">
                    <IonSearchbar
                        className="competitions-search"
                        value={query}
                        debounce={250}
                        placeholder="Buscar por nombre, sede o año"
                        onIonChange={function (event) {
                            setQuery(event.detail.value || "");
                        }}

                    />

                    <IonSegment
                        className="competitions-filter"
                        value={filter}
                        scrollable
                        onIonChange={function (event) {
                            setFilter(
                                (event.detail.value ||
                                    "all") as CompetitionFilter
                            );
                        }}
                    >
                        <IonSegmentButton value="all">
                            <span>Todas</span>
                        </IonSegmentButton>

                        <IonSegmentButton value="active">
                            <span>Activas</span>
                        </IonSegmentButton>

                        <IonSegmentButton value="upcoming">
                            <span>Próximas</span>
                        </IonSegmentButton>

                        <IonSegmentButton value="finished">
                            <span>Finalizadas</span>
                        </IonSegmentButton>
                    </IonSegment>

                    <div className="competitions-section__heading">
                        <div>
                            <span className="competitions-section__eyebrow">
                                TEMPORADAS
                            </span>

                            <h2>
                                {filter === "all"
                                    ? "Todas las competencias"
                                    : `Competencias ${getStatusLabel(
                                          filter
                                      ).toLocaleLowerCase("es-MX")}s`}
                            </h2>
                        </div>

                        {!loading && !error && (
                            <span className="competitions-counter">
                                {filteredCompetitions.length}
                            </span>
                        )}
                    </div>

                    {loading && (
                        <IonProgressBar
                            className="competitions-progress"
                            type="indeterminate"
                        />
                    )}

                    {!loading && error && (
                        <div className="competitions-empty-state">
                            <IonIcon icon={trophyOutline} />

                            <strong>No pudimos cargar las competencias</strong>

                            <span>{error}</span>

                            <IonButton
                                size="small"
                                onClick={function () {
                                    loadCompetitions(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        filteredCompetitions.length === 0 && (
                            <div className="competitions-empty-state">
                                <IonIcon icon={searchOutline} />

                                <strong>
                                    No encontramos competencias
                                </strong>

                                <span>
                                    Prueba con otro nombre, sede, año o filtro.
                                </span>

                                <IonButton
                                    fill="outline"
                                    size="small"
                                    onClick={function () {
                                        setQuery("");
                                        setFilter("all");
                                    }}
                                >
                                    Limpiar filtros
                                </IonButton>
                            </div>
                        )}

                    {!loading &&
                        !error &&
                        filteredCompetitions.map(function (competition) {
                            const status = getCompetitionStatus(competition);

                            return (
                                <IonCard
                                    className="competition-card"
                                    key={competition.id}
                                    button
                                    onClick={function () {
                                        history.push(
                                            `/results/${competition.id}`
                                        );
                                    }}
                                >
                                    <IonCardContent>
                                        <div className="competition-card__main">
                                            {competition.logo ? (
                                                <img
                                                    className="competition-card__logo"
                                                    src={competition.logo}
                                                    alt={`Logo de ${competition.nombre}`}
                                                />
                                            ) : (
                                                <div className="competition-card__logo-placeholder">
                                                    <IonIcon
                                                        icon={trophyOutline}
                                                    />
                                                </div>
                                            )}

                                            <div className="competition-card__info">
                                                <div className="competition-card__title-row">
                                                    <h3>
                                                        {competition.nombre}
                                                    </h3>

                                                    <IonBadge
                                                        className={`competition-card__status competition-card__status--${status}`}
                                                    >
                                                        {getStatusLabel(status)}
                                                    </IonBadge>
                                                </div>

                                                <p className="competition-card__dates">
                                                    <IonIcon
                                                        icon={calendarOutline}
                                                    />
                                                    {getSeasonDateText(
                                                        competition
                                                    )}
                                                </p>

                                                {competition.sede && (
                                                    <p className="competition-card__venue">
                                                        <IonIcon
                                                            icon={
                                                                locationOutline
                                                            }
                                                        />
                                                        {competition.sede}
                                                    </p>
                                                )}
                                            </div>

                                            <IonIcon
                                                className="competition-card__arrow"
                                                icon={chevronForward}
                                            />
                                        </div>

                                        <div className="competition-card__footer">
                                            <span>
                                                {competition.jornadas
                                                    ? `${competition.jornadas} jornadas`
                                                    : "Jornadas por confirmar"}
                                            </span>

                                            <span>Ver competencia</span>
                                        </div>
                                    </IonCardContent>
                                </IonCard>
                            );
                        })}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default Tournment;
